"use client";

import { ArrowRight, Disc, Users, Monitor, Heart } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurGrowthEngine() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pathways = [{
    numeral: "01",
    duration: "INSTANT",
    title: "Qualified Lead Flow",
    description: "Access a steady stream of high-intent insurance prospects ready for consultation. Our precision targeting ensures you spend your time closing deals rather than hunting for new opportunities.",
    instrumentIcon: Disc,
    location: "Digital",
    locationIcon: Monitor,
    price: "Performance-based"
  }, {
    numeral: "02",
    duration: "AUTOMATED",
    title: "Appointment Setting",
    description: "We handle the outreach and scheduling, filling your calendar with qualified appointments. Focus on your sales pitch while we manage the logistics of your customer acquisition pipeline.",
    instrumentIcon: Heart,
    location: "Remote",
    locationIcon: Monitor,
    price: "No upfront cost"
  }, {
    numeral: "03",
    duration: "SCALABLE",
    title: "Agency Outsourcing",
    description: "Scale your operations without the overhead. Our dedicated teams provide comprehensive sales support, customer outreach, and administrative assistance to help your agency grow efficiently.",
    instrumentIcon: Users,
    location: "On-demand",
    locationIcon: Monitor,
    price: "Custom pricing"
  }, {
    numeral: "04",
    duration: "STRATEGIC",
    title: "Growth Partnership",
    description: "A collaborative approach to scaling your insurance business. We align our performance metrics with your revenue goals, ensuring a mutually beneficial partnership built on sustainable growth.",
    instrumentIcon: Heart,
    location: "Dedicated",
    locationIcon: Monitor,
    price: "Revenue-share"
  }];
  return <section data-section-id="940" id="our-growth-engine" className="relative bg-background text-foreground py-20 md:py-28 lg:py-32 overflow-hidden border-b border-border/60">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-60" style={{
      background: "radial-gradient(ellipse at 50% 0%, hsl(220 60% 30% / 0.06), transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div ref={header.ref} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-primary font-bold">
            § 01 / GROWTH
          </p>
          <h2 className="mt-4 text-3xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground leading-[1.1]">
            Engineered for{" "}
            <span className="italic text-primary">scale.</span>
          </h2>
          <p className="mt-4 text-base md:text-lg lg:text-xl text-muted-foreground font-normal leading-relaxed max-w-2xl mx-auto">
            Precision solutions for insurance professionals.
          </p>
        </div>

        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {pathways.map((p, i) => {
          const InstrumentIcon = p.instrumentIcon;
          const LocationIcon = p.locationIcon;
          return <PathwayCard key={i} index={i} numeral={p.numeral} duration={p.duration} title={p.title} description={p.description} price={p.price} location={p.location} InstrumentIcon={InstrumentIcon} LocationIcon={LocationIcon} />;
        })}
        </div>
      </div>
    </section>;
}
function PathwayCard({
  index,
  numeral,
  duration,
  title,
  description,
  price,
  location,
  InstrumentIcon,
  LocationIcon
}: {
  index: number;
  numeral: string;
  duration: string;
  title: string;
  description: string;
  price: string;
  location: string;
  InstrumentIcon: React.ComponentType<{
    className?: string;
  }>;
  LocationIcon: React.ComponentType<{
    className?: string;
  }>;
}) {
  const reveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div data-index={index} ref={reveal.ref} className={`group relative flex flex-col justify-between bg-card border border-border/80 rounded-xl shadow-md hover:shadow-xl hover:border-primary/40 hover:-translate-y-1.5 transition-all duration-300 ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 100}ms`
  }}>
      <span aria-hidden="true" className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/80 via-accent to-primary/80 rounded-t-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="flex flex-col h-full p-8 md:p-10">
        <div className="flex items-center justify-between">
          <span className="font-mono text-4xl md:text-5xl font-extrabold tracking-tight text-primary">
            {numeral}
          </span>
          <span className="font-mono text-[11px] font-semibold tracking-[0.2em] uppercase text-foreground px-3 py-1.5 rounded-lg bg-secondary border border-border/60">
            {duration}
          </span>
        </div>

        <div className="mt-8 md:mt-10 flex-1 flex flex-col justify-between">
          <div>
            <h3 className="text-xl md:text-2xl lg:text-[1.65rem] font-bold text-foreground tracking-tight leading-snug">
              {title}
            </h3>
            <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-muted-foreground font-normal">
              {description}
            </p>
          </div>

          <div className="mt-8 pt-6 border-t border-border/70">
            <div className="flex flex-wrap items-center gap-3 md:gap-4">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary/80 border border-border/60 text-foreground">
                <div className="p-1 rounded-md bg-primary/10 text-primary border border-primary/20">
                  <InstrumentIcon className="h-3.5 w-3.5" />
                </div>
                <span className="text-xs font-semibold tracking-wide uppercase">
                  Performance
                </span>
              </div>

              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary/80 border border-border/60 text-foreground">
                <div className="p-1 rounded-md bg-primary/10 text-primary border border-primary/20">
                  <LocationIcon className="h-3.5 w-3.5" />
                </div>
                <span className="text-xs font-semibold tracking-wide uppercase">
                  {location}
                </span>
              </div>

              <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20 text-xs font-bold tracking-wide uppercase text-primary">
                {price}
              </div>
            </div>

            <div className="mt-6 pt-2">
              <Link to="/GetStartedPage" className="inline-flex items-center gap-2.5 text-xs md:text-sm font-bold tracking-[0.15em] uppercase text-primary hover:text-primary/80 transition-colors duration-200">
                <span>Get started now</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 ease-out group-hover:translate-x-1.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>;
}